import { createApp } from 'vue'
import { createPinia } from 'pinia'
import AppShell from './app-shell/AppShell.vue'
import router from './router'

import './styles/style.scss'
import i18n from './i18n'
import authService from './api/authService'

// Import the Auth0 configuration
import authConfig from '../auth_config.json'
import { Auth0Plugin, setSharedPlanTokenCartoV3 } from './auth'
import type { RouteLocationRaw } from 'vue-router'
import { getRedirectUri, getRequireComponent } from './constants'
import { initializeFirebaseApp, AuthManager } from '@workspaces/services'
import { getAppMetadata } from './plan/metadata/metadata'
import {
  getAccessTokenForSharedPlan,
  getBasemapsConfig,
  getCartoAppConfiguration,
  getCartoConfigurationSSO,
  getLayersPrecedence,
} from './plan/metadata/metadata.helper'
import { TrackJS } from 'trackjs'
import BrowserCartoAuthorizer from './auth/carto-auth'
import BrowserEnvironmentResolver from './helpers/environment.helper'
import { initializeUserPermissionManager } from '@/helpers/auth-browser.helper'
import SharedPlanPasswordManager from '@/helpers/sharedPlanPassword.helper'
import { Loader } from '@googlemaps/js-api-loader'
import {
  isGoogleMapsBasemap,
  resolveGoogleMapsApiKey,
} from './helpers/google-maps.helper'
import { setupZIndexValuesForLayers } from '@/layers/layers-z-index.helper'
;(async () => {
  try {
    console.debug('🟩 Preparing APP ...')
    const environmentResolver = BrowserEnvironmentResolver.getInstance()
    const isTesting = environmentResolver.isTesting()
    console.debug(`😈 Testing mode: ${isTesting}`)
    const metadata = getAppMetadata()
    window.document.title = metadata.app_title

    const isLocalhost =
      location.host.indexOf('localhost') === 0 ||
      location.host.indexOf('127.0.0.1') === 0

    // TrackJS configuration ----------
    const trackJsToken = import.meta.env.VITE_APP_TRACKJS_TOKEN
    if (isTesting || isLocalhost) {
      console.debug('🟩 TrackJS: disabled')
    } else if (!trackJsToken) {
      console.warn('🟥 TrackJS: disabled (VITE_APP_TRACKJS_TOKEN is not set)')
      throw new Error('VITE_APP_TRACKJS_TOKEN is not set')
    } else {
      const version = `${import.meta.env.VITE_APP_ENV}-${metadata.flavour}`
      console.debug(`🟩 TrackJS: enabled | version: ${version}`)
      TrackJS.install({
        token: trackJsToken,
        application: 'carto-ps-radarview-us',
        console: { display: true, watch: ['error', 'warn'] },
        network: { error: false },
        version,
      })
    }

    const layersPrecedence = getLayersPrecedence(metadata)
    console.debug(
      '🗺️ Setting up layers precendece, from top to bottom: ',
      layersPrecedence,
    )
    setupZIndexValuesForLayers(layersPrecedence)

    // Load Google Maps API
    const basemapConfig = getBasemapsConfig(metadata)
    if (basemapConfig) {
      const googleMapsIsAvailable = basemapConfig.options.find((e) =>
        isGoogleMapsBasemap(e.id),
      )
      const googleMapsApiKey = resolveGoogleMapsApiKey(
        import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        {
          isGoogleMapsConfigured: Boolean(googleMapsIsAvailable),
          isTesting,
          isLocalhost,
        },
      )
      if (googleMapsApiKey) {
        console.debug('🟩 Google Maps: loading ...')
        const loader = new Loader({
          apiKey: googleMapsApiKey,
          version: 'weekly',
          language: 'en',
          libraries: ['places'],
        })

        await loader.importLibrary('maps')
        console.debug('🟩 Google Maps: loaded')
      }
    }

    // Carto configuration ---------
    const cartoAppConfiguration = getCartoAppConfiguration(
      metadata,
      environmentResolver,
    )
    console.debug('🟩 CARTO app configuration', cartoAppConfiguration)

    const url = window.location.href
    const queryParams = new URLSearchParams(url)
    const sharedPlan = queryParams.get('shared')

    // Create the Vue 3 app and register the core plugins. Pinia must be
    // installed before the auth flow / login, which reach the stores via
    // useXStore().
    const app = createApp(AppShell)
    app.use(createPinia())
    app.use(i18n)

    // Auth0 configuration ---------
    if (sharedPlan !== 'true') {
      console.debug('🟧 Auth0: registering plugin')
      const redirectUri = getRedirectUri()
      app.use(Auth0Plugin, {
        domain: authConfig.domain,
        clientId: cartoAppConfiguration.clientId,
        redirectUri,
        cacheLocation: authConfig.cacheLocation,
        scopes: authConfig.scopes,
        audience: authConfig.audience,
        organization: getCartoConfigurationSSO(metadata, environmentResolver),
        onRedirectCallback: (appState: { targetUrl: RouteLocationRaw }) => {
          router.push(
            appState && appState.targetUrl
              ? appState.targetUrl
              : window.location.pathname,
          )
        },
      })
    } else {
      console.debug('🟩 Auth0: Not registering plugin [Shared plan]')
      const sharedPlanId = queryParams.get('planId')
      if (sharedPlanId === null) {
        throw new Error('Shared plan id is required')
      }
      const sharedPlanPasswordManager = SharedPlanPasswordManager.getInstance()
      await sharedPlanPasswordManager.init(
        metadata,
        environmentResolver,
        sharedPlanId,
      )

      // deck.gl 9 removed the global setDefaultCredentials that used to carry
      // the share token. Every CARTO source now reads its token from the auth
      // service (AuthManager.getToken -> getAccessTokenCartoV3), so store the
      // share token there for the anonymous shared-plan session.
      const shareTileToken = getAccessTokenForSharedPlan(environmentResolver)
      if (shareTileToken) {
        setSharedPlanTokenCartoV3(shareTileToken)
      }

      // The authenticated flows init AuthManager (authService.login, Auth0
      // callback, Plan/Dashboard mount). Shared-plan mode runs none of those,
      // yet deck.gl 9 sources call AuthManager.getToken() as soon as they mount.
      // Initialize it here (idempotent — first authorizer wins) so the share
      // token is served before any component mounts.
      AuthManager.getInstance().init(new BrowserCartoAuthorizer())
    }

    // Error handler
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    window.onerror = (message, source, lineno, colno, error) => {
      // message: error message (string). Available as event (sic!) in HTML onerror="" handler.
      // source: URL of the script where the error was raised (string)
      // lineno: Line number where error was raised (number)
      // colno: Column number for the line where the error occurred (number)
      // error: Error Object (object)
      onError({
        err: message,
        source,
        info: `in line ${lineno}:${colno}`,
      })
    }

    app.config.errorHandler = (err, instance, info) => {
      // err: error trace
      // instance: component in which error occured
      // info: Vue specific error information such as lifecycle hooks, events etc.
      onError({
        err,
        source: instance,
        info,
      })
    }

    const requireComponent = getRequireComponent()

    Object.entries(requireComponent).forEach(([fileName, component]) => {
      // Get component config
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const componentConfig = component as any
      const fileNameSplitted = fileName.split('/')
      const chunk = fileNameSplitted.pop() || ''
      const componentName = chunk.replace(/\.\w+$/, '')

      // Register component globally
      app.component(componentName, componentConfig.default || componentConfig)
    })

    // Firebase configuration ---------

    initializeFirebaseApp(metadata, environmentResolver)

    if (sharedPlan !== 'true') {
      console.debug('🟧 Login ...')
      await authService.login()
    } else {
      console.debug('🟧 Login: Not logging in [Shared plan]')
      await initializeUserPermissionManager()
    }

    // Running Vue App ---------

    // Install the router last: vue-router runs the initial navigation (and its
    // beforeEach guard) as soon as the router is installed. Auth state — notably
    // the admin role resolved by authService.login() — must be restored first.
    app.use(router)

    app.mount('#app')

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    function onError({ err, source, info }) {
      /* eslint-disable */
      console.error('Error: ', err.message, err)
      console.error('Source: ', source)
      console.error('Extra info: ', info)
      /* eslint-enable */
    }
  } catch (err) {
    console.error(err)
    process.exit(1)
  }
})()
